/**
 * BankAccountsList — list of REAL bank accounts pulled from
 * ``/openbanking/connections``, with balance, IBAN tail, sync status
 * and a per-row "Sync ora" affordance.
 *
 * This is the SOTA replacement of the legacy "Open banking · Collegato"
 * single-line indicator. Each row exposes the information an SMB
 * operator actually cares about: HOW MUCH is on each account, WHEN was
 * the last refresh, and IS the sync HEALTHY.
 */

import { Building2, Loader2, RefreshCw } from "lucide-react";
import type * as React from "react";

import { Button, ListItemEyebrow, ListItemRow, SenderAvatar } from "@ando/ui";
import { msg } from "@lingui/core/macro";
import type { I18n } from "@lingui/core";
import { Trans, useLingui } from "@lingui/react/macro";

import type { BankAccountDTO } from "@ando/core";


function formatBalance(cents: number | null, currency: string): string {
  if (cents === null || !Number.isFinite(cents)) return "—";
  return new Intl.NumberFormat("it-IT", {
    style: "currency",
    currency,
    maximumFractionDigits: 2,
  }).format(cents / 100);
}


/** Italian relative time for "last synced 2h ago". */
function formatLastSynced(iso: string | null, i18n: I18n): string | null {
  if (!iso) return null;
  const t = Date.parse(iso);
  if (!Number.isFinite(t)) return null;
  const diffMs = Date.now() - t;
  if (diffMs < 0) return i18n._(msg`appena`);
  const min = Math.round(diffMs / 60000);
  if (min < 1) return i18n._(msg`appena`);
  if (min < 60) return i18n._(msg`${min}m fa`);
  const h = Math.round(min / 60);
  if (h < 24) return i18n._(msg`${h}h fa`);
  const d = Math.round(h / 24);
  if (d < 30) return i18n._(msg`${d}g fa`);
  return new Intl.DateTimeFormat("it-IT", {
    day: "2-digit",
    month: "short",
  }).format(new Date(t));
}


function ibanTail(iban: string | null): string {
  if (!iban) return "";
  const trimmed = iban.replace(/\s/g, "");
  return trimmed.length > 4 ? `••${trimmed.slice(-4)}` : trimmed;
}


export interface BankAccountsListProps {
  readonly accounts: readonly BankAccountDTO[];
  readonly onTriggerSync?: (bankAccountId: string) => void;
  readonly syncingId?: string | null;
}


export const BankAccountsList: React.FC<BankAccountsListProps> = ({
  accounts,
  onTriggerSync,
  syncingId,
}) => {
  const { t, i18n } = useLingui();
  if (accounts.length === 0) return null;
  return (
    <ul className="-mx-5 flex flex-col" aria-label={t`Conti bancari`}>
      {accounts.map((account, idx) => {
        const lastSynced = formatLastSynced(account.last_synced_at, i18n);
        const tail = ibanTail(account.iban);
        const isHealthy = account.sync_status === "active";
        const isSyncing = syncingId === account.id;
        return (
          <ListItemRow
            key={account.id}
            itemId={account.id}
            index={idx}
            avatar={
              <SenderAvatar
                toneBg="bg-emerald-50"
                toneFg="text-emerald-700"
                size={28}
                icon={<Building2 size={14} strokeWidth={2} aria-hidden />}
                title={account.institution_name ?? t`Conto bancario`}
                className="mt-0.5"
              />
            }
            eyebrow={
              <>
                <ListItemEyebrow><Trans>Conto</Trans></ListItemEyebrow>
                {tail ? (
                  <span className="font-mono text-[10px] text-ink-3 tabular-nums">
                    {tail}
                  </span>
                ) : null}
              </>
            }
            title={
              account.institution_name ??
              account.account_name ??
              t`Conto bancario`
            }
            preview={
              account.account_name && account.institution_name
                ? account.account_name
                : undefined
            }
            trailing={
              <span
                className={
                  account.balance_cents === null
                    ? "text-[12px] text-ink-3 tabular-nums"
                    : "font-semibold text-[13px] text-ink-1 tabular-nums"
                }
              >
                {formatBalance(account.balance_cents, account.currency)}
              </span>
            }
            pills={
              <>
                {lastSynced ? (
                  <span className="inline-flex items-center rounded-full border border-hairline bg-cream/50 px-1.5 py-0.5 font-medium text-[10px] text-ink-3 tabular-nums">
                    <Trans>Aggiornato {lastSynced}</Trans>
                  </span>
                ) : null}
                {!isHealthy ? (
                  <span className="inline-flex items-center rounded-full border border-rose-200 px-1.5 py-0.5 font-medium text-[10px] text-rose-700">
                    {account.sync_status === "paused"
                      ? t`In pausa`
                      : t`Errore sync`}
                  </span>
                ) : null}
                {onTriggerSync ? (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => onTriggerSync(account.id)}
                    disabled={isSyncing}
                    className="h-6 gap-1 px-2 font-medium text-[10.5px] text-ink-3 hover:bg-black/[0.04] hover:text-ink-1"
                  >
                    {isSyncing ? (
                      <Loader2
                        size={11}
                        strokeWidth={2}
                        aria-hidden
                        className="animate-spin"
                      />
                    ) : (
                      <RefreshCw size={11} strokeWidth={2} aria-hidden />
                    )}
                    {isSyncing ? t`Sync…` : t`Sync ora`}
                  </Button>
                ) : null}
              </>
            }
            onClick={() => {
              /* No detail route yet — placeholder for future drill-down. */
            }}
            highlighted={!isHealthy}
          />
        );
      })}
    </ul>
  );
};
BankAccountsList.displayName = "BankAccountsList";
